import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Ticket, ShieldCheck, Eye, ArrowRight, Receipt, CircleNotch, ArrowLeft,
} from "@phosphor-icons/react";
import Logomark from "@/components/Logomark";
import UserMenu from "@/components/UserMenu";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";

const STATUS_STYLE = {
  paid: "text-emerald-800 bg-emerald-50 border-emerald-200",
  pending: "text-amber-800 bg-amber-50 border-amber-200",
  expired: "text-gray-500 bg-gray-50 border-gray-200",
};

function PassStatusCard({ user }) {
  const navigate = useNavigate();
  const active = user.role === "admin" || user.tier === "full";
  const unlimited = user.role === "admin" || (user.tier === "full" && user.builds_remaining == null);
  return (
    <div className={`border p-6 sm:p-7 ${active ? "border-gray-950 bg-white" : "border-amber-300 bg-amber-50/50"}`}
      data-testid="account-pass-status">
      <div className="flex items-center gap-2 mb-3">
        {active
          ? <ShieldCheck size={20} weight="duotone" className="text-emerald-700" />
          : <Eye size={20} weight="duotone" className="text-amber-700" />}
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500">Current access</p>
      </div>
      <h2 className="font-display font-black text-2xl tracking-tight mb-1">
        {user.role === "admin" ? "Admin — full access"
          : active ? (user.pass_label || "Full access")
          : user.pass_expired ? "Pass expired — Read-Only Vault"
          : "Free Taster"}
      </h2>
      <div className="text-sm text-gray-600 space-y-1 mt-3">
        {active && user.pass_expires_at && (
          <p data-testid="account-pass-expiry">Active until <b className="text-gray-900">{fmtDate(user.pass_expires_at)}</b></p>
        )}
        <p data-testid="account-builds-remaining">
          Plan builds remaining:{" "}
          <b className="text-gray-900">
            {unlimited ? "Unlimited" : active ? (user.builds_remaining ?? 0)
              : user.free_build_used || user.pass_expired ? 0 : "1 free build"}
          </b>
        </p>
        {!active && (
          <p className="text-gray-500">
            {user.pass_expired
              ? "Your plans are safe and viewable. Any new pass reactivates building, Atlas and exports instantly."
              : "Upgrade any time — every pass unlocks the full toolkit."}
          </p>
        )}
      </div>
      <button onClick={() => navigate("/pricing")} data-testid="account-pricing-cta"
        className={`mt-5 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold transition-colors ${
          active ? "border border-gray-900 text-gray-900 hover:bg-gray-50" : "bg-gray-950 text-white hover:bg-gray-700"
        }`}>
        {active ? "Extend or stack a pass" : "See passes"} <ArrowRight size={14} weight="bold" />
      </button>
    </div>
  );
}

export default function Account() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [history, setHistory] = useState(null);

  useEffect(() => {
    api.paymentHistory().then(setHistory).catch(() => setHistory([]));
  }, []);

  if (!user) return null;

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="account-page">
      <header className="border-b border-gray-200 px-6 sm:px-10 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/library")} className="flex items-center gap-2" data-testid="account-home-link">
          <Logomark size={24} />
          <span className="font-display font-black text-2xl tracking-tight">Xartis</span>
        </button>
        <UserMenu />
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-6 sm:px-10 py-12">
        <button onClick={() => navigate("/library")} data-testid="account-back-link"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors mb-6">
          <ArrowLeft size={14} weight="bold" /> My Plans
        </button>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-gray-500 mb-2">Account</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-1">{user.name || user.email}</h1>
          <p className="text-sm text-gray-500 mb-8">{user.email}</p>

          <PassStatusCard user={user} />

          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Receipt size={18} weight="duotone" className="text-gray-500" />
              <h2 className="font-display font-bold text-lg tracking-tight">Purchase history</h2>
            </div>
            {history === null ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-8" data-testid="history-loading">
                <CircleNotch size={16} className="animate-spin" /> Loading…
              </div>
            ) : history.length === 0 ? (
              <div className="border border-dashed border-gray-300 p-8 text-center" data-testid="history-empty">
                <Ticket size={24} weight="duotone" className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No purchases yet. Your receipts will appear here.</p>
              </div>
            ) : (
              <div className="border border-gray-200 overflow-x-auto" data-testid="history-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-gray-500 font-semibold">Date</th>
                      <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-gray-500 font-semibold">Pass</th>
                      <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-gray-500 font-semibold text-right">Amount</th>
                      <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-gray-500 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((t) => (
                      <tr key={t.reference + t.created_at} className="border-t border-gray-100" data-testid="history-row">
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(t.created_at)}</td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-gray-900">{t.pass_label}</span>
                          {t.builds != null && <span className="text-gray-400 text-xs"> · {t.builds} builds</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-gray-900 whitespace-nowrap">
                          ${Number(t.amount).toFixed(2)} <span className="text-gray-400 text-xs">{t.currency}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[t.payment_status] || STATUS_STYLE.pending}`}>
                            {t.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="font-mono text-[11px] text-gray-400 mt-4">
              A tax receipt is emailed to {user.email} after every successful payment. Prices include 10% GST.
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
